import lottie from 'lottie-web';

import type { AnimationItem } from 'lottie-web';
import type Phaser from 'phaser';

type LottieCanvasSpriteConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	sourceRoot: HTMLElement;
	textureKey: string;
	animationData: unknown;
	canvasWidth: number;
	canvasHeight: number;
	loop: boolean;
	lazy?: boolean;
	renderAtDisplaySize?: boolean;
	subframe?: boolean;
	originX?: number;
	originY?: number;
	onComplete?: () => void;
};

const LOTTIE_TEXTURE_SCALE = 2;
const DISPLAY_SIZE_RESERVE = 2;

export class LottieCanvasSprite {
	private animation: AnimationItem | null = null;
	private createAnimation: (() => AnimationItem) | null = null;
	private textureDirty = true;
	private lastDrawnFrame?: number;
	private readonly handleDrawnFrame = () => {
		const frame = this.animation?.currentFrame;
		if (frame === this.lastDrawnFrame) return;
		this.lastDrawnFrame = frame;
		this.textureDirty = true;
	};
	private readonly sourceContainer: HTMLDivElement;
	private readonly handleDomLoaded: () => void;
	private readonly handleComplete: () => void;
	private texture: Phaser.Textures.Texture | null = null;
	private image: Phaser.GameObjects.Image | null = null;
	private displayWidth = 1;
	private displayHeight = 1;
	private visible = false;
	private playing = false;
	private flipX = false;
	private destroyed = false;

	constructor({
		scene,
		parent,
		sourceRoot,
		textureKey,
		animationData,
		canvasWidth,
		canvasHeight,
		loop,
		lazy = false,
		renderAtDisplaySize = false,
		subframe = true,
		originX = 0.5,
		originY = 1,
		onComplete,
	}: LottieCanvasSpriteConfig) {
		this.sourceContainer = document.createElement('div');
		this.sourceContainer.style.width = `${canvasWidth}px`;
		this.sourceContainer.style.height = `${canvasHeight}px`;
		sourceRoot.appendChild(this.sourceContainer);

		this.createAnimation = () => {
			if (renderAtDisplaySize && this.displayWidth > 1 && this.displayHeight > 1) {
				// Retain detail for camera zoom without exceeding the authored canvas dimensions.
				this.sourceContainer.style.width = `${Math.min(
					canvasWidth,
					Math.ceil(this.displayWidth * DISPLAY_SIZE_RESERVE),
				)}px`;
				this.sourceContainer.style.height = `${Math.min(
					canvasHeight,
					Math.ceil(this.displayHeight * DISPLAY_SIZE_RESERVE),
				)}px`;
			}
			const animation = lottie.loadAnimation({
				container: this.sourceContainer,
				renderer: 'canvas',
				loop,
				autoplay: false,
				animationData,
				rendererSettings: {
					clearCanvas: true,
					dpr: LOTTIE_TEXTURE_SCALE,
					preserveAspectRatio: 'xMidYMid meet',
				},
			});
			if (!subframe) animation.setSubframe(false);
			return animation;
		};

		this.handleDomLoaded = () => {
			if (this.destroyed || !this.animation || this.image) {
				return;
			}

			const canvas = this.sourceContainer.querySelector('canvas');
			// Lottie creates the element before its backing buffer is initialized.
			if (!(canvas instanceof HTMLCanvasElement) || canvas.width === 0 || canvas.height === 0) {
				return;
			}

			// A plain texture avoids CanvasTexture’s unused full-canvas pixel readback.
			const texture = scene.textures.create(textureKey, canvas);
			if (!texture) {
				return;
			}

			texture.add('__BASE', 0, 0, 0, canvas.width, canvas.height);
			this.texture = texture;
			this.image = scene.add
				.image(0, 0, textureKey)
				.setOrigin(originX, originY)
				.setDisplaySize(this.displayWidth, this.displayHeight)
				.setFlipX(this.flipX)
				.setVisible(this.visible);
			parent.add(this.image);
			this.refreshTexture();
		};
		this.handleComplete = () => {
			if (this.destroyed) {
				return;
			}

			this.playing = false;
			this.textureDirty = true;
			this.refreshTexture();
			onComplete?.();
		};

		if (!lazy) this.initializeAnimation();
	}

	private initializeAnimation() {
		if (this.destroyed || this.animation || !this.createAnimation) return;
		this.animation = this.createAnimation();
		this.createAnimation = null;
		this.animation.addEventListener('DOMLoaded', this.handleDomLoaded);
		this.animation.addEventListener('complete', this.handleComplete);
		this.animation.addEventListener('drawnFrame', this.handleDrawnFrame);
		this.handleDomLoaded();
	}

	update() {
		this.handleDomLoaded();
		if (!this.destroyed && this.visible && this.playing) {
			this.refreshTexture();
		}
	}

	setDisplaySize(width: number, height: number) {
		this.displayWidth = width;
		this.displayHeight = height;
		this.image?.setDisplaySize(width, height);
	}

	setVisible(visible: boolean) {
		if (this.visible === visible) return;
		this.visible = visible;
		this.image?.setVisible(visible);
		if (visible) {
			this.refreshTexture();
		}
	}

	setFlipX(flipX: boolean) {
		this.flipX = flipX;
		this.image?.setFlipX(flipX);
	}

	setSpeed(speed: number) {
		this.initializeAnimation();
		this.animation?.setSpeed(speed);
	}

	play() {
		this.playing = true;
		this.initializeAnimation();
		this.animation?.play();
	}

	playFromStart() {
		this.playing = true;
		this.initializeAnimation();
		this.animation?.goToAndPlay(0, true);
	}

	pause() {
		this.playing = false;
		this.animation?.pause();
	}

	stop() {
		this.playing = false;
		this.animation?.stop();
		this.textureDirty = true;
		this.refreshTexture();
	}

	destroy() {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.playing = false;
		const image = this.image;
		const texture = this.texture;
		this.image = null;
		this.texture = null;
		const canvas = this.sourceContainer.querySelector('canvas');
		this.animation?.removeEventListener('DOMLoaded', this.handleDomLoaded);
		this.animation?.removeEventListener('complete', this.handleComplete);
		this.animation?.removeEventListener('drawnFrame', this.handleDrawnFrame);
		this.animation?.destroy();
		this.animation = null;
		this.createAnimation = null;
		image?.destroy();
		if (this.isTextureLive(texture)) {
			texture.destroy();
		}
		if (canvas instanceof HTMLCanvasElement) {
			canvas.width = 0;
			canvas.height = 0;
		}
		this.sourceContainer.remove();
	}

	private refreshTexture() {
		const texture = this.texture;
		if (this.textureDirty && this.isTextureLive(texture)) {
			texture.source[0].update();
			this.textureDirty = false;
		}
	}

	private isTextureLive(texture: Phaser.Textures.Texture | null): texture is Phaser.Textures.Texture {
		return texture?.manager?.get(texture.key) === texture;
	}
}
